import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Check, X } from "lucide-react";
import { Link } from "react-router-dom";
import { useState } from "react";

const Pricing = () => {
  const [showComparison, setShowComparison] = useState(false);

  const handlePlanClick = () => {
    window.open("https://forms.gle/yWSKnznK4DGmTxqn9", "_blank");
  };

  const plans = [
    {
      name: "🆓 Free Forever",
      price: "$0",
      period: "/month", 
      description: "Perfect for trying out our platform",
      features: [
        "250 images per month",
        "Standard resolution",
        "Basic AI models",
        "Batch upload (3 images)",
        "7-day history",
        "Personal use only"
      ],
      notIncluded: [
        "HD quality",
        "Commercial rights",
        "API access",
        "Team features",
        "Watermark removal"
      ],
      popular: false,
      cta: "Start Free",
      highlight: "Free Forever"
    },
    {
      name: "🎨 Hobby",
      price: "$7.99",
      period: "/month",
      description: "Ideal starter plan for aspiring creators and personal projects",
      features: [
        "5,000 images per month",
        "HD quality",
        "All standard AI models",
        "Batch upload (10 images)",
        "30-day history",
        "Watermark removal",
        "Full commercial rights"
      ],
      notIncluded: [
        "Premium options",
        "API access",
        "Team features",
        "Latest model releases"
      ],
      popular: false,
      cta: "Upgrade to Hobby"
    },
    {
      name: "🚀 Creator",
      price: "$19.99",
      period: "/month",
      description: "Most popular choice for content creators",
      features: [
        "16,000 images per month",
        "HD + Premium options",
        "All AI models + latest releases",
        "Batch upload (50 images)",
        "60-day history",
        "Advanced editing",
        "Full rights + resale permissions",
        "API Coming Soon",
        "Up to 2 team members"
      ],
      notIncluded: [
        "Ultra HD",
        "Custom integrations",
        "Analytics dashboard"
      ],
      popular: true,
      cta: "Upgrade to Creator"
    },
    {
      name: "💼 Professional",
      price: "$49.99",
      period: "/month",
      description: "Perfect for professional creators and small teams",
      features: [
        "50,000 images per month",
        "All options including Ultra HD",
        "All AI models",
        "Unlimited batch upload",
        "Unlimited history",
        "Full + resale rights",
        "API Coming Soon",
        "Up to 5 team members",
        "Custom integrations",
        "Analytics dashboard"
      ],
      notIncluded: [
        "24/7 dedicated support",
        "SSO integration",
        "SLA guarantees"
      ],
      popular: false,
      cta: "Upgrade to Professional"
    },
    {
      name: "🏢 Business",
      price: "$99.99",
      period: "/month",
      description: "Enterprise-grade solution for large teams",
      features: [
        "150,000 images per month",
        "All + custom resolutions",
        "24/7 + dedicated success manager",
        "Advanced team management",
        "Audit logs",
        "Enterprise API (5,000 calls/month)",
        "Unlimited team members",
        "SSO integration",
        "Advanced security",
        "SLA guarantees"
      ],
      notIncluded: [],
      popular: false,
      cta: "Contact Sales"
    },
    {
      name: "Pay as You Go",
      price: "$0.04",
      period: "/image",
      description: "Flexible pricing with volume discounts",
      features: [
        "No monthly commitment",
        "HD quality",
        "All AI models",
        "Batch upload (20 images)",
        "90-day history",
        "Watermark removal",
        "Full commercial rights",
        "Volume discounts available"
      ],
      volumePricing: [
        "100+ images: $0.035/image",
        "500+ images: $0.030/image", 
        "1000+ images: $0.025/image"
      ],
      notIncluded: [
        "Team collaboration",
        "Priority support",
        "API access"
      ],
      popular: false,
      cta: "Start Pay-as-Go"
    }
  ];

  const comparisonData = {
    features: [
      { name: "Images per month", free: "250", hobby: "5,000", creator: "16,000", professional: "50,000", business: "150,000", payAsGo: "Pay per use" },
      { name: "Image Quality", free: "Standard", hobby: "HD", creator: "HD + Premium", professional: "Ultra HD", business: "Ultra HD + Custom", payAsGo: "HD" },
      { name: "AI Models", free: "Basic", hobby: "All Standard", creator: "All + Latest", professional: "All", business: "All", payAsGo: "All" },
      { name: "Batch Upload", free: "3 images", hobby: "10 images", creator: "50 images", professional: "Unlimited", business: "Unlimited", payAsGo: "20 images" },
      { name: "History Retention", free: "7 days", hobby: "30 days", creator: "60 days", professional: "Unlimited", business: "Unlimited", payAsGo: "90 days" },
      { name: "Commercial Rights", free: "Personal only", hobby: "Full", creator: "Full + Resale", professional: "Full + Resale", business: "Full + Resale", payAsGo: "Full" },
      { name: "Team Members", free: "0", hobby: "0", creator: "2", professional: "5", business: "Unlimited", payAsGo: "0" },
      { name: "API Access", free: "✗", hobby: "✗", creator: "Coming Soon", professional: "Coming Soon", business: "Enterprise", payAsGo: "✗" },
      { name: "Watermark Removal", free: "✗", hobby: "✓", creator: "✓", professional: "✓", business: "✓", payAsGo: "✓" },
      { name: "Support", free: "Community", hobby: "Email", creator: "Priority", professional: "Priority", business: "24/7 Dedicated", payAsGo: "Standard" }
    ]
  };

  return (
    <section id="pricing" className="py-10 sm:py-14 md:py-18 lg:py-20 xl:py-24 bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 relative overflow-hidden">
      {/* Enhanced Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808008_1px,transparent_1px),linear-gradient(to_bottom,#80808008_1px,transparent_1px)] bg-[size:14px_14px] sm:bg-[size:16px_16px] md:bg-[size:20px_20px] lg:bg-[size:24px_24px]"></div>
        <div className="absolute inset-0 bg-gradient-to-br from-blue-600/5 via-transparent to-purple-600/5"></div>
      </div>

      {/* Optimized floating orbs */}
      <div className="absolute top-1/4 left-1/4 w-24 h-24 sm:w-32 sm:h-32 md:w-48 md:h-48 lg:w-64 lg:h-64 bg-blue-500/10 rounded-full blur-3xl animate-pulse"></div>
      <div className="absolute bottom-1/4 right-1/4 w-32 h-32 sm:w-48 sm:h-48 md:w-64 md:h-64 lg:w-96 lg:h-96 bg-purple-500/10 rounded-full blur-3xl animate-pulse"></div>
      <div className="absolute top-1/2 left-1/2 w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 lg:w-32 lg:h-32 bg-cyan-500/15 rounded-full blur-2xl animate-bounce"></div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 xl:px-12 relative z-10 max-w-8xl">
        {/* Enhanced Header Section */}
        <div className="text-center mb-10 sm:mb-12 md:mb-16 lg:mb-20 max-w-5xl mx-auto">
          <Badge className="mb-4 sm:mb-5 md:mb-6 bg-gradient-to-r from-blue-600/15 to-purple-600/15 text-blue-300 border-blue-500/20 text-xs sm:text-sm md:text-base px-3 sm:px-4 md:px-5 py-1.5 sm:py-2 font-medium">
            💎 Transparent Pricing
          </Badge>
          <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-bold text-white mb-3 sm:mb-4 md:mb-5 lg:mb-6 leading-tight tracking-tight">
            Choose Your{" "}
            <span className="bg-gradient-to-r from-blue-400 via-purple-400 to-cyan-400 bg-clip-text text-transparent">
              Perfect Plan
            </span>
          </h2>
          <p className="text-sm sm:text-base md:text-lg lg:text-xl text-gray-300 max-w-4xl mx-auto leading-relaxed font-light">
            Start free and scale as you grow. No hidden fees, no surprises.
          </p>
        </div>

        {/* Enhanced Square Pricing Cards Grid - Reduced size with equal spacing */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 md:gap-8 lg:gap-10 mb-10 sm:mb-12 md:mb-14 lg:mb-16 max-w-6xl mx-auto px-2 sm:px-4">
          {plans.map((plan, index) => (
            <Card 
              key={index} 
              className={`group relative bg-white/[0.03] backdrop-blur-xl border-white/[0.08] hover:bg-white/[0.08] hover:border-white/20 transition-all duration-700 flex flex-col min-h-[420px] sm:min-h-[450px] md:min-h-[480px] lg:min-h-[500px] touch-manipulation hover:scale-[1.02] hover:shadow-2xl hover:shadow-purple-500/10 rounded-2xl ${
                plan.popular ? 'ring-2 ring-blue-500/40 shadow-lg shadow-blue-500/20 lg:scale-[1.02] border-blue-500/30' : ''
              } ${plan.name.includes('Free') ? 'ring-2 ring-green-500/40 shadow-lg shadow-green-500/20 border-green-500/30' : ''}`}
            >
              {/* Enhanced gradient border effect */}
              {(plan.popular || plan.name.includes('Free')) && (
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-blue-600/10 via-purple-600/10 to-cyan-600/10 opacity-0 group-hover:opacity-100 transition-opacity duration-700 -z-10 blur-xl"></div>
              )}
              
              {/* Enhanced badges */}
              {plan.popular && (
                <Badge className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-blue-600 to-purple-600 text-white text-xs md:text-sm shadow-lg animate-pulse px-4 py-1.5 font-semibold z-10">
                  ⭐ MOST POPULAR
                </Badge>
              )}
              {plan.name.includes('Free') && (
                <Badge className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-green-600 to-emerald-600 text-white text-xs md:text-sm shadow-lg px-4 py-1.5 font-semibold z-10">
                  {plan.highlight}
                </Badge>
              )}
              
              {/* Enhanced Card Header */}
              <CardHeader className="text-center pb-3 flex-shrink-0 px-4 sm:px-5 pt-6 sm:pt-7">
                <CardTitle className="text-white text-base sm:text-lg lg:text-xl font-bold group-hover:text-blue-300 transition-colors duration-500 mb-3 leading-snug">
                  {plan.name}
                </CardTitle>
                <div className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white group-hover:scale-105 transition-transform duration-500 mb-3">
                  {plan.price}
                  <span className="text-xs lg:text-sm text-gray-400 block mt-1 font-normal">{plan.period}</span>
                </div>
                <p className="text-gray-400 text-xs sm:text-sm lg:text-base leading-relaxed group-hover:text-gray-300 transition-colors duration-500 px-1 mb-2">
                  {plan.description}
                </p>
              </CardHeader>
              
              {/* Enhanced Card Content */}
              <CardContent className="flex-1 flex flex-col justify-between px-4 sm:px-5 pb-6 gap-4">
                <div className="space-y-2 flex-1 overflow-y-auto max-h-[220px] custom-scrollbar pr-2">
                  {/* Enhanced Features */}
                  {plan.features.map((feature, idx) => (
                    <div key={idx} className="flex items-start text-gray-300 group-hover:text-white transition-colors duration-500 py-0.5">
                      <Check className="w-3 h-3 text-green-400 mr-2 flex-shrink-0 group-hover:text-green-300 transition-colors duration-500 mt-0.5" />
                      <span className="text-xs sm:text-sm lg:text-base leading-relaxed font-medium">{feature}</span>
                    </div>
                  ))}
                  
                  {/* Enhanced Volume Pricing */}
                  {plan.volumePricing && (
                    <div className="mt-4 p-3 bg-blue-500/10 rounded-lg border border-blue-500/20 backdrop-blur-sm">
                      <p className="text-blue-300 text-xs font-semibold mb-2">Volume Discounts:</p>
                      {plan.volumePricing.map((pricing, idx) => (
                        <div key={idx} className="flex items-center text-blue-200 mb-1">
                          <Check className="w-2.5 h-2.5 text-blue-400 mr-2 flex-shrink-0" />
                          <span className="text-xs lg:text-sm font-medium">{pricing}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Enhanced Not Included Features */}
                  {plan.notIncluded.slice(0, 3).map((feature, idx) => (
                    <div key={idx} className="flex items-start text-gray-500 py-0.5">
                      <X className="w-3 h-3 text-gray-500 mr-2 flex-shrink-0 mt-0.5" />
                      <span className="text-xs sm:text-sm lg:text-base leading-relaxed">{feature}</span>
                    </div>
                  ))}
                </div>
                
                {/* Enhanced CTA Button */}
                <div className="mt-auto pt-3">
                  <Button 
                    onClick={handlePlanClick}
                    className={`w-full text-xs sm:text-sm lg:text-base py-3 lg:py-4 h-10 lg:h-12 touch-manipulation active:scale-95 transition-all duration-500 group-hover:scale-[1.02] group-hover:shadow-lg font-semibold tracking-wide rounded-xl ${
                      plan.popular 
                        ? 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 shadow-lg shadow-blue-500/20 hover:shadow-blue-500/30' 
                        : plan.name.includes('Free')
                        ? 'bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 shadow-lg shadow-green-500/20 hover:shadow-green-500/30'
                        : 'bg-white/8 hover:bg-white/15 text-white border border-white/15 hover:border-white/30 hover:shadow-lg hover:shadow-white/5 backdrop-blur-sm'
                    }`}
                  >
                    {plan.cta}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-12 md:mb-16 px-4">
          <Button
            onClick={() => setShowComparison(!showComparison)}
            variant="outline"
            className="bg-white/10 hover:bg-white/20 text-white border-white/30 px-6 lg:px-8 py-4 lg:py-5 h-14 lg:h-16 w-full sm:w-auto text-base lg:text-lg touch-manipulation active:scale-95 transition-transform font-medium"
            noGlow
          >
            {showComparison ? 'Hide' : 'Compare All Plans'}
          </Button>
          <Button 
            onClick={handlePlanClick}
            className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white px-6 lg:px-8 py-4 lg:py-5 h-14 lg:h-16 w-full sm:w-auto text-base lg:text-lg touch-manipulation active:scale-95 transition-transform font-medium"
            noGlow
          >
            Schedule Enterprise Demo
          </Button>
        </div>

        {/* Enhanced Comparison Table */}
        {showComparison && (
          <Card className="bg-white/[0.06] backdrop-blur-md border-white/15 mt-8 lg:mt-12 overflow-hidden">
            <CardHeader className="bg-gradient-to-r from-blue-600/10 to-purple-600/10 border-b border-white/10">
              <CardTitle className="text-white text-center text-xl sm:text-2xl lg:text-3xl font-bold">
                Complete Feature Comparison
              </CardTitle>
            </CardHeader>
            <CardContent className="px-2 sm:px-4 lg:px-6 py-6">
              <div className="overflow-x-auto">
                <table className="w-full text-xs sm:text-sm lg:text-base min-w-[900px]">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="text-left text-white py-3 sm:py-4 lg:py-5 px-2 sm:px-3 lg:px-4 font-bold">Feature</th>
                      <th className="text-center text-white py-3 sm:py-4 lg:py-5 px-2 sm:px-3 lg:px-4 font-bold">Free</th>
                      <th className="text-center text-white py-3 sm:py-4 lg:py-5 px-2 sm:px-3 lg:px-4 font-bold">Hobby</th>
                      <th className="text-center text-white py-3 sm:py-4 lg:py-5 px-2 sm:px-3 lg:px-4 font-bold">Creator</th>
                      <th className="text-center text-white py-3 sm:py-4 lg:py-5 px-2 sm:px-3 lg:px-4 font-bold">Professional</th>
                      <th className="text-center text-white py-3 sm:py-4 lg:px-4 font-bold">Business</th>
                      <th className="text-center text-white py-3 sm:py-4 lg:py-5 px-2 sm:px-3 lg:px-4 font-bold">Pay as Go</th>
                    </tr>
                  </thead>
                  <tbody>
                    {comparisonData.features.map((feature, idx) => (
                      <tr key={idx} className="border-b border-white/5 hover:bg-white/[0.03] transition-colors duration-300">
                        <td className="text-gray-300 py-3 sm:py-4 lg:py-5 px-2 sm:px-3 lg:px-4 font-semibold">{feature.name}</td>
                        <td className="text-center text-gray-300 py-3 sm:py-4 lg:py-5 px-2 sm:px-3 lg:px-4">{feature.free}</td>
                        <td className="text-center text-gray-300 py-3 sm:py-4 lg:py-5 px-2 sm:px-3 lg:px-4">{feature.hobby}</td>
                        <td className="text-center text-gray-300 py-3 sm:py-4 lg:py-5 px-2 sm:px-3 lg:px-4">{feature.creator}</td>
                        <td className="text-center text-gray-300 py-3 sm:py-4 lg:py-5 px-2 sm:px-3 lg:px-4">{feature.professional}</td>
                        <td className="text-center text-gray-300 py-3 sm:py-4 lg:py-5 px-2 sm:px-3 lg:px-4">{feature.business}</td>
                        <td className="text-center text-gray-300 py-3 sm:py-4 lg:py-5 px-2 sm:px-3 lg:px-4">{feature.payAsGo}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="mt-6 lg:mt-8 p-4 lg:p-6 bg-blue-500/8 border border-blue-500/15 rounded-lg backdrop-blur-sm">
                <p className="text-blue-300 text-sm lg:text-base leading-relaxed">
                  <strong>💡 Getting Started:</strong> All new users start with the Free plan (250 images/month). 
                  Upgrade anytime to unlock more features and higher limits. Pay-as-You-Go offers volume discounts for flexible usage.
                </p>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
      
      <style>
        {`
          .custom-scrollbar::-webkit-scrollbar {
            width: 4px;
          }
          .custom-scrollbar::-webkit-scrollbar-track {
            background: rgba(255, 255, 255, 0.05);
            border-radius: 2px;
          }
          .custom-scrollbar::-webkit-scrollbar-thumb {
            background: rgba(255, 255, 255, 0.2);
            border-radius: 2px;
          }
          .custom-scrollbar::-webkit-scrollbar-thumb:hover {
            background: rgba(255, 255, 255, 0.3);
          }
        `}
      </style>
    </section>
  );
};

export default Pricing;
